import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Phone, MessageSquare, Zap } from "lucide-react";
import { useDevicesStore } from '@/stores/devices-store';
import { useConfigurationStore } from '@/stores/configuration-store';
import { useCallRoutingStore } from '@/stores/call-routing-store';
import { GreetingCard } from '@/components/call-routing/greeting-card';
import { GoodbyeCard } from '@/components/call-routing/goodbye-card';
import { RoutingTargetList } from '@/components/call-routing/routing-target-list';
import { useEffect, useMemo, useState } from 'react';
import { useToast } from '@/hooks/use-toast';

export default function MdsConfigurationPage() {
  const { currentDevice, isMdsDevice, getCurrentDeviceSerialId } = useDevicesStore();
  const { languages, voices, initBaseConfiguration } = useConfigurationStore();
  const { 
    initCallRoutingConfiguration,
    getDefaultGreetingMessageObject,
    getDefaultGoodbyeMessageObject,
    setGreetingMessage,
    setGoodbyeMessage,
    callRoutingConfigurationObject,
    isUpdating 
  } = useCallRoutingStore();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  // Load configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      const { useAuthStore } = require('@/stores/auth-store');
      const headers = useAuthStore.getState().getApiHeaders();
      if (headers && headers['X-AAI-Token']) {
        try {
          setIsLoading(true);
          await initBaseConfiguration(headers);
          
          // Load call routing if MDS device
          if (isMdsDevice()) {
            await initCallRoutingConfiguration();
          }
        } catch (error) {
          console.error('Failed to load configuration:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };
    loadConfig();
  }, [initBaseConfiguration, initCallRoutingConfiguration, isMdsDevice]);

  const deviceInfo = useMemo(() => {
    if (!currentDevice) return null;
    
    return {
      name: currentDevice.name,
      id: getCurrentDeviceSerialId(),
      type: isMdsDevice() ? 'MDS Gerät' : 'Standard Gerät',
      config: currentDevice.deviceConfiguration || {}
    };
  }, [currentDevice, getCurrentDeviceSerialId, isMdsDevice]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                MDS Konfiguration
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Call Routing und Systemeinstellungen
              </p>
            </div>
            {deviceInfo && (
              <Badge variant="default" className="bg-brand-primary">{deviceInfo.type}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {!isMdsDevice() && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Diese Seite ist nur für MDS-Geräte verfügbar. Dies ist ein Standard-Gerät.
            </p>
          </div>
        )}
        
        <Tabs defaultValue={isMdsDevice() ? "routing" : "general"} className="w-full">
          <TabsList className={`grid w-full mb-6 ${isMdsDevice() ? 'grid-cols-4' : 'grid-cols-3'}`}>
            {isMdsDevice() && (
              <TabsTrigger value="routing" className="text-sm">
                <Zap className="w-4 h-4 mr-2" />
                Routing
              </TabsTrigger>
            )}
            <TabsTrigger value="general">Allgemein</TabsTrigger>
            <TabsTrigger value="languages">Sprachen ({languages.length})</TabsTrigger>
            <TabsTrigger value="voices">Stimmen ({voices.length})</TabsTrigger>
          </TabsList>
          
          {/* Routing Tab - Only for MDS */}
          {isMdsDevice() && (
            <TabsContent value="routing">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Zap className="h-5 w-5 text-brand-primary" />
                      Call Routing Konfiguration
                    </span>
                    <Badge variant="secondary">MDS</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoading || isUpdating ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-primary mx-auto mb-4"></div>
                      <p className="text-gray-600">Lade Routing-Konfiguration...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Greeting Message */}
                      <GreetingCard
                        title="Begrüßungsnachricht"
                        message={getDefaultGreetingMessageObject().message}
                        language={getDefaultGreetingMessageObject().language}
                        onUpdate={async (data) => {
                          try {
                            await setGreetingMessage(data.message, data.language)
                            toast({
                              title: 'Erfolgreich gespeichert',
                              description: 'Begrüßungsnachricht wurde aktualisiert',
                            })
                          } catch (error) {
                            toast({
                              variant: 'destructive',
                              title: 'Fehler',
                              description: 'Nachricht konnte nicht gespeichert werden',
                            })
                          }
                        }}
                      />

                      {/* Routing Targets */}
                      <RoutingTargetList
                        targets={callRoutingConfigurationObject.selectionElements?.[0]?.possibleSelections || {}}
                        onAdd={() => {
                          toast({
                            title: 'Info',
                            description: 'Add Routing Target Dialog wird in einer zukünftigen Version verfügbar sein',
                          })
                        }}
                        onEdit={() => {
                          toast({
                            title: 'Info',
                            description: 'Edit Funktion wird in einer zukünftigen Version verfügbar sein',
                          })
                        }}
                        onDelete={() => {
                          toast({
                            title: 'Info',
                            description: 'Delete Funktion wird in einer zukünftigen Version verfügbar sein',
                          })
                        }}
                      />

                      {/* Goodbye Message */}
                      <GoodbyeCard
                        title="Verabschiedungsnachricht"
                        message={getDefaultGoodbyeMessageObject().message}
                        language={getDefaultGoodbyeMessageObject().language}
                        onUpdate={async (data) => {
                          try {
                            await setGoodbyeMessage(data.message, data.language)
                            toast({
                              title: 'Erfolgreich gespeichert',
                              description: 'Verabschiedungsnachricht wurde aktualisiert',
                            })
                          } catch (error) {
                            toast({
                              variant: 'destructive',
                              title: 'Fehler',
                              description: 'Nachricht konnte nicht gespeichert werden',
                            })
                          }
                        }}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
          
          <TabsContent value="general">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5 text-gray-600" />
                    Geräteinformationen
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {deviceInfo ? (
                    <>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">Name</span>
                        <span className="text-sm font-medium">{deviceInfo.name}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">ID</span>
                        <span className="text-sm font-mono">{deviceInfo.id}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">Typ</span>
                        <Badge variant="secondary">{deviceInfo.type}</Badge>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Settings className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm">Kein Gerät ausgewählt</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Phone className="h-5 w-5 text-gray-600" />
                    Konfiguration
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {deviceInfo && Object.keys(deviceInfo.config).length > 0 ? (
                    Object.entries(deviceInfo.config).slice(0, 5).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600">{key}</span>
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {String(value)}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Settings className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm">Keine Konfiguration verfügbar</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          <TabsContent value="languages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Verfügbare Sprachen</span>
                  <Badge variant="secondary">{languages.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {languages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Keine Sprachen geladen</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Sprachkonfiguration wird vom Server geladen
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {languages.map((lang: any) => (
                      <div
                        key={lang.code}
                        className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <p className="font-medium text-gray-900">{lang.name}</p>
                        <p className="text-sm text-gray-600 mt-1">{lang.code}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="voices">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Verfügbare Stimmen</span>
                  <Badge variant="secondary">{voices.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {voices.length === 0 ? (
                  <div className="text-center py-12">
                    <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 font-medium">Keine Stimmen geladen</p>
                    <p className="text-sm text-gray-500 mt-2">
                      Stimmenkonfiguration wird vom Server geladen
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {voices.map((voice: any) => (
                      <div
                        key={voice.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{voice.name}</p>
                          <p className="text-sm text-gray-600 mt-1">{voice.id}</p>
                        </div>
                        <Badge variant="outline">{voice.language}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
